import {
  ChatChannelInstall,
  ChatPlatform,
  OwnSlackApp,
  OwnSlackAppCredentials,
  OwnSlackAppState,
  SlackInstallResult,
  TeamsInstallResult,
} from "@/types/agent/chat-channels";
import { ApiService } from "../api/ApiService";

class ChatChannelService {
  list(agentId: string): Promise<ChatChannelInstall[]> {
    return ApiService.invoke<ChatChannelInstall[]>({
      resource: "chat-channels",
      action: "list",
      data: { agentId },
    });
  }

  /** Null while the platform isn't available yet (Teams, until the app is published). */
  async getInstallLink(agentId: string, platform: ChatPlatform): Promise<string | null> {
    const { url } = await ApiService.invoke<{ url: string | null }>({
      resource: "chat-channels",
      action: "install-link",
      data: { agentId, platform },
    });
    return url;
  }

  setPaused(agentId: string, installId: string, paused: boolean): Promise<ChatChannelInstall> {
    return ApiService.invoke<ChatChannelInstall>({
      resource: "chat-channels",
      action: "set-paused",
      data: { agentId, installId, paused },
    });
  }

  async disconnect(agentId: string, installId: string): Promise<void> {
    await ApiService.invoke({
      resource: "chat-channels",
      action: "disconnect",
      data: { agentId, installId },
    });
  }

  getOwnApp(agentId: string): Promise<OwnSlackAppState> {
    return ApiService.invoke<OwnSlackAppState>({
      resource: "chat-channels",
      action: "own-app-get",
      data: { agentId },
    });
  }

  createOwnApp(agentId: string): Promise<OwnSlackApp> {
    return ApiService.invoke<OwnSlackApp>({
      resource: "chat-channels",
      action: "own-app-create",
      data: { agentId },
    });
  }

  saveOwnAppCredentials(agentId: string, credentials: OwnSlackAppCredentials): Promise<OwnSlackApp> {
    return ApiService.invoke<OwnSlackApp>({
      resource: "chat-channels",
      action: "own-app-save",
      data: { agentId, ...credentials },
    });
  }

  markOwnAppManifestUpdated(agentId: string): Promise<OwnSlackApp> {
    return ApiService.invoke<OwnSlackApp>({
      resource: "chat-channels",
      action: "own-app-save",
      data: { agentId, manifestUpdated: true },
    });
  }

  async removeOwnApp(agentId: string): Promise<void> {
    await ApiService.invoke({
      resource: "chat-channels",
      action: "own-app-remove",
      data: { agentId },
    });
  }

  /** Public: resolves an own app's install link for whoever opens it. */
  async getOwnAppInstallUrl(chatAppId: string): Promise<string> {
    const { url } = await ApiService.invoke<{ url: string }>(
      { resource: "chat-channels", action: "own-app-install-url", data: { chatAppId } },
      "public",
    );
    return url;
  }

  /** Public: the customer finishing the install, usually without a Help Genie account. */
  completeSlackInstall(code: string, state: string): Promise<SlackInstallResult> {
    return ApiService.invoke<SlackInstallResult>(
      { resource: "chat-channels", action: "slack-oauth", data: { code, state } },
      "public",
    );
  }

  /** Public: the customer finishing the Microsoft sign-in, usually without a Help Genie account. */
  completeTeamsInstall(code: string, state: string): Promise<TeamsInstallResult> {
    return ApiService.invoke<TeamsInstallResult>(
      { resource: "chat-channels", action: "teams-oauth", data: { code, state } },
      "public",
    );
  }
}

export const chatChannelService = new ChatChannelService();
